<template>
  <div id="leadersList">
    <div
      v-if="leaders.length > 0 || invitationList.length > 0"
      class="data mainCard"
    >
      <div class="header mainCard">
        <div class="searchWrap">
          <el-row
            class="searchTools"
            :class="['mobile', 'tablet'].includes($mq) ? 'mobileMode' : ''"
            style="width: 100%; flex-wrap: wrap;"
            type="flex"
            justify="space-between"
            :gutter="8"
          >
            <el-col :span="responsiveCol.one">
              <div
                class="selectWrap"
                :class="['mobile', 'tablet'].includes($mq) ? 'mobileMode' : ''"
              >
                <div
                  class="searchTitle defaultCursor"
                  :class="['mobile', 'tablet'].includes($mq) ? 'mobileMode' : ''"
                >
                  {{ $t('common.sortBy') }}
                </div>
                <el-select
                  v-model="sortOrder"
                  :placeholder="$t('common.select')"
                  class="searchSelect"
                >
                  <el-option
                    v-for="item in sortOptions"
                    :key="item.value"
                    :label="item.label"
                    :value="item.value"
                  />
                </el-select>
              </div>
            </el-col>
            <el-col :span="responsiveCol.two">
              <div
                class="selectWrap"
                :class="['mobile', 'tablet'].includes($mq) ? 'mobileMode' : ''"
              >
                <div
                  class="searchTitle defaultCursor"
                  :class="['mobile', 'tablet'].includes($mq) ? 'mobileMode' : ''"
                >
                  {{ $t('common.filterBy') }}
                </div>
                <el-select
                  v-model="selectedFilter"
                  :placeholder="$t('common.select')"
                  class="searchSelect"
                >
                  <el-option
                    v-for="item in filterOptions"
                    :key="item.value"
                    :label="item.label"
                    :value="item.value"
                  />
                </el-select>
              </div>
            </el-col>
            <el-col :span="responsiveCol.three">
              <div
                class="inputWrap"
                :class="['mobile', 'tablet'].includes($mq) ? 'mobileMode' : 'deskMode'"
              >
                <el-input
                  v-model="searchInput"
                  class="clearInput searchText"
                  suffix-icon="el-icon-search"
                  clearable
                  placeholder="Search"
                />
              </div>
            </el-col>
          </el-row>
        </div>
      </div>

      <div
        class="secondHeader"
        :class="['mobile', 'tablet'].includes($mq) ? 'mobileMode' : 'deskMode'"
      >
        <div class="leaderCount defaultCursor">
          <div
            v-if="user.profile && user.profile.is_ac"
            style="padding-bottom: 10px;"
          >
            {{ $t('coach.leader.currentGcgEngagement') }}: {{ gcgEngagementCount }}
          </div>
          <div v-if="canInviteLeader">
            {{ $t('coach.leader.currentPersonalEngagement') }}: {{ currentInvitedCount }}
          </div>
        </div>
        <el-button
          v-if="canInviteLeader"
          class="inviteBtn"
          type="primary"
          round
          @click="toggleInviteButton"
        >
          {{ $t('coach.leader.invitation.manageEngagements') }}
        </el-button>
      </div>

      <template
        v-if="formattedLeaders.length > 0 || formattedInvitationList.length > 0"
      >
        <div v-if="formattedLeaders.length > 0">
          <div
            v-for="(leader, key) in formattedLeaders"
            :key="key"
            v-loading="loading"
            class="leader"
            @click.stop.prevent="goToDetails(leader)"
          >
            <LeaderSimple
              :class="leader.curr_engagement_status === 'exception' ? 'exceptedLeader' : null"
              :leader="leader"
              :is-details-view="false"
            />
          </div>
        </div>

        <div v-if="formattedInvitationList.length > 0">
          <div
            v-for="(leader, key) in formattedInvitationList"
            :key="`${key}`"
          >
            <InvitedLeader
              :leader="leader"
              @deleted="removeDeletedInvitation"
            />
          </div>
        </div>
      </template>

      <div
        v-else
        class="noData defaultCursor"
      >
        <el-card
          v-loading="loading"
          shadow="hover"
          class="mainCard clear-card"
        >
          <span v-if="!loading">{{ $t('coach.leader.noData') }}</span>
        </el-card>
      </div>
    </div>

    <div
      v-else
      class="noData defaultCursor"
    >
      <div
        class="mainCard secondHeader"
        :class="['mobile', 'tablet'].includes($mq) ? 'mobileMode' : 'deskMode'"
      >
        <el-button
          v-if="canInviteLeader"
          class="inviteBtn"
          type="primary"
          round
          @click="toggleInviteButton"
        >
          {{ $t('coach.leader.invitation.manageEngagements') }}
        </el-button>
      </div>
      <el-card
        v-loading="loading"
        shadow="hover"
        class="mainCard clear-card"
        style="text-align: center;"
      >
        <span v-if="!loading">{{ $t('common.noData') }}</span>
      </el-card>
    </div>

    <InviteLeader
      :show.sync="invitationModal"
      :invitation-status="invitationStatus"
      @userUpdated="userUpdatedFromModal"
      @newUsers="getInvitation"
    />

    <InvitationAvailableAlert
      :show.sync="invitationAvailableAlert"
    />
  </div>
</template>

<script>
import { mapState } from 'vuex';
import api from '@/api';
import LeaderSimple from './LeaderSimple.vue';
import InvitedLeader from './InvitedLeader.vue';
import InviteLeader from './widgets/InviteLeader.vue';
import InvitationAvailableAlert from './widgets/InvitationAvailableAlert.vue';

export default {
  components: {
    LeaderSimple,
    InvitedLeader,
    InviteLeader,
    InvitationAvailableAlert,
  },

  data() {
    return {
      leaders: [],
      invitationList: [],
      multipleLoading: [],
      sortOptions: [
        { value: 'nextMeeting', label: this.$t('coach.sort.nextMeeting') },
        { value: 'sessionAsc', label: this.$t('coach.sort.sessionAsc') },
        { value: 'sessionDesc', label: this.$t('coach.sort.sessionDesc') },
        { value: 'nameAsc', label: this.$t('coach.sort.nameAsc') },
        { value: 'nameDesc', label: this.$t('coach.sort.nameDesc') },
      ],
      // filterOptions: [
      //   { value: 'all', label: 'All leaders' },
      //   { value: 'gcg', label: 'GCG Engagements' },
      //   { value: 'invited', label: 'Invited leaders' },
      //   { value: 'pending', label: 'Pending registrations' },
      // ],
      sortOrder: 'nextMeeting',
      selectedFilter: 'all',
      searchInput: '',
      invitationModal: false,
      invitationAvailableAlert: false,
      invitationStatus: null,
      currentInvitedCount: 0,
    };
  },

  computed: {
    ...mapState({
      user: (state) => state.user,
    }),

    filterOptions() {
      const options = [
        { value: 'all', label: this.$t('coach.filter.leaderAll') },
        { value: 'gcg', label: this.$t('coach.filter.engagementGcg') },
      ];

      if (this.canInviteLeader) {
        options.push({ value: 'invited', label: this.$t('coach.filter.leaderInvited') });
      }

      options.push({ value: 'pending', label: this.$t('coach.filter.pendingRegistrations') });

      return options;
    },

    canInviteLeader() {
      return process.env.VUE_APP_CAN_INVITE_LEADER !== 'false';
    },

    loading() {
      return this.multipleLoading.length > 0;
    },

    formattedLeaders() {
      const { leaders } = this;

      const list = leaders
        .filter((el) => {
          // filter option = all, gcg, invited, pending
          let flag = true;
          if (this.searchInput !== '') {
            flag = el.first_name.toUpperCase().includes(this.searchInput.toUpperCase())
                || el.last_name.toUpperCase().includes(this.searchInput.toUpperCase());
          }
          if (this.selectedFilter === 'gcg') {
            flag = !el.is_invited;
          }
          if (this.selectedFilter === 'invited') {
            flag = el.is_invited;
          }
          if (this.selectedFilter === 'pending') {
            flag = false;
          }
          return flag;
        }).sort((a, b) => {
          const firstSession = a.session_id === 'on_boarding' ? -1 : a.session_id.split('_')[1];
          const secondSession = b.session_id === 'on_boarding' ? -1 : b.session_id.split('_')[1];
          const firstFullName = `${a.first_name} ${a.last_name}`;
          const secondFullName = `${b.first_name} ${b.last_name}`;
          switch (this.sortOrder) {
            case 'nextMeeting':
              return a.next_meeting > b.next_meeting ? 1 : -1;
            case 'sessionAsc':
              return firstSession - secondSession;
            case 'sessionDesc':
              return secondSession - firstSession;
            case 'nameAsc':
              return firstFullName > secondFullName ? 1 : -1;
            case 'nameDesc':
              return firstFullName < secondFullName ? 1 : -1;
            default:
              return 0;
          }
        });

      return list;
    },

    formattedInvitationList() {
      const { invitationList } = this;

      const list = invitationList
        .filter((el) => {
          // filter option = all, gcg, invited, pending
          let flag = true;
          if (this.searchInput !== '') {
            flag = el.email.toUpperCase().includes(this.searchInput.toUpperCase());
          }
          if (['gcg', 'invited'].includes(this.selectedFilter)) {
            flag = false;
          }
          if (el.is_registered) {
            // Always filter hide what registered.
            flag = false;
          }
          return flag;
        });

      return list;
    },

    responsiveCol() {
      const col = {};
      if (['mobile', 'tablet'].includes(this.$mq)) {
        col.one = 12;
        col.two = 12;
        col.three = 24;
      } else {
        col.one = 8;
        col.two = 8;
        col.three = 8;
      }
      return col;
    },

    invitationFlag() {
      let flag = false;
      if (this.invitationStatus && this.invitationStatus.split('/')) {
        flag = (this.invitationStatus.split('/')[1] - this.invitationStatus.split('/')[0]) > 0;
      }

      return flag;
    },

    gcgEngagementCount() {
      let count = 0;

      if (this.leaders && this.leaders.length) {
        count = this.leaders.filter((el) => !el.is_invited).length;
      }

      return count;
    },
  },

  created() {
    this.getLeaders();
    this.getInvitation();
  },

  mounted() {
    console.log(this.$route.query);
    if (this.$route.query.manageEngagement) {
      this.invitationModal = true;
    }

    this.$EventBus.$on('subscriptionUpdated', () => {
      this.userUpdatedFromModal();
    });
  },

  beforeDestroy() {
    this.$EventBus.$off('subscriptionUpdated');
  },

  methods: {
    userUpdatedFromModal() {
      this.getLeaders();
      this.getInviteStatus();
    },

    goToDetails(leader) {
      this.$router.push({ name: 'myLeaderDetail', params: { id: leader.uuid } }).catch((err) => {});
    },

    async getLeaders() {
      try {
        this.multipleLoading.push('run');
        const response = await api.getCoachLeaders();
        this.leaders = response.data;
        const filtered = response.data.filter((el) => el.is_invited);
        this.currentInvitedCount = filtered.length;
      } catch (err) {
        console.log(err);
        if (err.msg) {
          this.$message.error(err.msg);
        }
      } finally {
        this.multipleLoading.pop();
      }
    },

    async getInvitation() {
      try {
        this.multipleLoading.push('run');
        const params = { page_size: 100 };
        this.getInviteStatus();
        const response = await api.getLeaderInvitation(params);
        this.invitationList = response.data.results;
      } catch (err) {
        console.log(err);
        if (err.msg) {
          this.$message.error(err.msg);
        }
      } finally {
        this.multipleLoading.pop();
      }
    },

    async getInviteStatus() {
      try {
        this.multipleLoading.push('run');
        const response = await api.getInviteStatus();
        this.invitationStatus = response.data.status;
      } catch (err) {
        console.log(err);
      } finally {
        this.multipleLoading.pop();
      }
    },

    toggleInviteButton() {
      // if (this.invitationFlag) {
      //   this.invitationModal = true;
      // } else {
      //   this.invitationAvailableAlert = true;
      // }
      this.invitationModal = true;
    },

    removeDeletedInvitation(pk) {
      this.invitationList = this.invitationList.filter((el) => el.pk !== pk);
    },
  },
};
</script>

<style lang="scss" scoped>
#leadersList {
  .data {
    .header {
      margin-bottom: 15px;
      color: var(--themeFontColor);

      .searchWrap {
        display: flex;
        flex-wrap: wrap;
        align-items: center;

        .searchTools {
          display: flex;
          justify-content: space-between;
          align-items: flex-end;
          width: calc(100% - 75px);

          .selectWrap {
            display: flex;
            align-items: center;
            flex-wrap: wrap;

            &.mobileMode {
              .searchSelect {
                width: 100%;
              }
            }

            .searchTitle {
              font-weight: bold;
              padding-right: 10px;
              box-sizing: border-box;
              width: 80px;

              &.mobileMode {
                width: 100%;
              }
            }
          }

          .inputWrap {
            width: 100%;
            text-align: right;

            &.deskMode {
              .searchText {
                max-width: 250px;
              }
            }
          }

          .el-input {
            border-bottom: 1px solid var(--themeFontColor);
            width: 100%;
          }

          &.mobileMode {
            width: 100%;
            margin-top: 10px;
          }
        }
      }
    }

    .leader {
      cursor: pointer;

      .exceptedLeader {
        opacity: 0.5;
        transition: 200ms ease-in-out opacity;
        &:hover {
          opacity: 1;
        }
      }
    }

    .secondHeader {
      display: flex;
      width: 100%;
      justify-content: space-between;
      align-items: center;
      padding: 10px 0;

      &.mobileMode {
        flex-wrap: wrap;

        .leaderCount,
        .inviteBtn {
          width: 100%;
        }

        .inviteBtn {
          margin-top: 15px;
        }
      }

      .leaderCount {
        padding: 10px 0 30px;
        font-weight: bold;
        color: var(--themeFontColor);
      }
    }
  }

  .noData {
    margin-bottom: 15px;

    .secondHeader {
      display: flex;
      width: 100%;
      justify-content: flex-end;
      align-items: center;
      padding: 10px 0;

      &.mobileMode {
        .inviteBtn {
          width: 100%;
          margin-top: 15px;
        }
      }
    }
  }
}
</style>

<style lang="scss">
#leadersList .data .header .searchWrap .el-select .el-input__inner {
  border-radius: 20px;
  background-color: transparent;
  color: var(--themeFontColor);
}
</style>
